import React, { useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container, VStack, Heading, Text, Box, Badge, HStack,
  Divider, SimpleGrid, Icon, Table, Thead, Tbody, Tr, Th, Td,
  UnorderedList, ListItem, Button, Link, useColorMode,
} from "@chakra-ui/react";
import { FiArrowLeft, FiCheck, FiX, FiClock, FiUsers, FiShield, FiDollarSign } from "react-icons/fi";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const SafeCryptoCompetition: React.FC = () => {
  // Force light mode on this public page
  const { colorMode, setColorMode } = useColorMode();
  useEffect(() => {
    if (colorMode === "dark") setColorMode("light");
  }, [colorMode, setColorMode]);
  const competitors = [
    {
      name: "Coinbase Custody",
      fee: "0.5% annual on AUM",
      minDeposit: "$1M+",
      audience: "Institutional",
      timelock: false,
      guardians: false,
      socialEngProtection: false,
      selfCustody: false,
      chains: "BTC, ETH, 200+ tokens",
      weakness: "Centralised — Coinbase holds your keys. If they get hacked or go bankrupt, you lose everything. No time-lock protection against social engineering.",
    },
    {
      name: "BitGo",
      fee: "0.25-0.5% annual",
      minDeposit: "$100K+",
      audience: "Institutional",
      timelock: false,
      guardians: false,
      socialEngProtection: false,
      selfCustody: false,
      chains: "BTC, ETH, 600+ tokens",
      weakness: "Multi-sig but still centralised custody. No mandatory time delays. Enterprise-only pricing and complexity.",
    },
    {
      name: "Fireblocks",
      fee: "$10K+/month flat",
      minDeposit: "Enterprise only",
      audience: "Institutional",
      timelock: true,
      guardians: false,
      socialEngProtection: false,
      selfCustody: false,
      chains: "40+ blockchains",
      weakness: "MPC technology is strong but $120K+/year minimum. Policy engine can add delays but no human guardian network. Not accessible to individuals.",
    },
    {
      name: "Casa",
      fee: "$130-$2,500/year flat",
      minDeposit: "None",
      audience: "Individual",
      timelock: false,
      guardians: false,
      socialEngProtection: false,
      selfCustody: true,
      chains: "BTC only",
      weakness: "3-of-5 multisig requires multiple hardware wallets. No time-lock. If attacker convinces you to sign with 3 devices, funds are gone instantly. BTC only — no ETH or tokens.",
    },
    {
      name: "Anchorage Digital",
      fee: "0.4-0.8% annual",
      minDeposit: "$5M+",
      audience: "Institutional",
      timelock: true,
      guardians: false,
      socialEngProtection: false,
      selfCustody: false,
      chains: "BTC, ETH, SOL, 50+ tokens",
      weakness: "Federally chartered crypto bank — strong but minimum $5M. Time-locks available but no distributed human verification network.",
    },
  ];

  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.lg" py={10}>
        <VStack spacing={6} align="stretch">
          {/* Back link */}
          <Link as={RouterLink} to="/updates/safe-crypto-custody" _hover={{ textDecoration: "none" }}>
            <Button variant="ghost" size="sm" leftIcon={<FiArrowLeft />} color="gray.500">
              Back to Safe Crypto Custody
            </Button>
          </Link>

          {/* Header */}
          <VStack spacing={3} align="start">
            <HStack>
              <Badge colorScheme="orange">Safe Crypto</Badge>
              <Badge colorScheme="blue">Competition Analysis</Badge>
            </HStack>
            <Heading size="lg" color="#2c5282">How We Compare</Heading>
            <Text color="gray.600">
              Every existing custody solution protects against technical hacks. None of them protect against the #1 way people actually lose crypto: social engineering.
            </Text>
          </VStack>

          <Divider />

          {/* The Core Difference */}
          <Box bg="blue.50" p={5} borderRadius="md" border="1px solid" borderColor="blue.200">
            <Heading size="md" color="#2b6cb0" mb={3}>The Core Difference</Heading>
            <Text color="gray.600" mb={3}>
              Traditional custody asks: <strong>"How do we store keys securely?"</strong>
            </Text>
            <Text color="gray.600" mb={3}>
              Safe Crypto asks: <strong>"How do we protect against the moment a human gets tricked?"</strong>
            </Text>
            <Text color="gray.600" fontSize="sm">
              Every competitor below uses some form of multi-sig or MPC to split keys across devices. That's necessary but not sufficient — because social engineering attacks don't steal keys, they trick the owner into authorising a legitimate transaction. Time-locks and guardian networks are the only defence against that.
            </Text>
          </Box>

          <Divider />

          {/* Feature Comparison Table */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>Feature Comparison</Heading>
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Feature</Th>
                    <Th textAlign="center">Safe Crypto</Th>
                    <Th textAlign="center">Coinbase</Th>
                    <Th textAlign="center">BitGo</Th>
                    <Th textAlign="center">Fireblocks</Th>
                    <Th textAlign="center">Casa</Th>
                    <Th textAlign="center">Anchorage</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td fontWeight="bold">Mandatory Time Lock</Td>
                    <Td textAlign="center" color="green.600"><Icon as={FiCheck} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="orange.400">Optional</Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="orange.400">Optional</Td>
                  </Tr>
                  <Tr bg="gray.50">
                    <Td fontWeight="bold">Guardian Verification</Td>
                    <Td textAlign="center" color="green.600"><Icon as={FiCheck} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">Social Engineering Protection</Td>
                    <Td textAlign="center" color="green.600"><Icon as={FiCheck} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                  </Tr>
                  <Tr bg="gray.50">
                    <Td fontWeight="bold">Self-Custody</Td>
                    <Td textAlign="center" color="green.600"><Icon as={FiCheck} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="green.600"><Icon as={FiCheck} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">Individual Access</Td>
                    <Td textAlign="center" color="green.600"><Icon as={FiCheck} /></Td>
                    <Td textAlign="center" color="red.400">$1M min</Td>
                    <Td textAlign="center" color="red.400">$100K min</Td>
                    <Td textAlign="center" color="red.400">Enterprise</Td>
                    <Td textAlign="center" color="green.600"><Icon as={FiCheck} /></Td>
                    <Td textAlign="center" color="red.400">$5M min</Td>
                  </Tr>
                  <Tr bg="gray.50">
                    <Td fontWeight="bold">Multi-Chain</Td>
                    <Td textAlign="center" color="green.600">BTC, ETH, Gold</Td>
                    <Td textAlign="center" color="green.600">200+</Td>
                    <Td textAlign="center" color="green.600">600+</Td>
                    <Td textAlign="center" color="green.600">40+</Td>
                    <Td textAlign="center" color="orange.400">BTC only</Td>
                    <Td textAlign="center" color="green.600">50+</Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">Duress Code</Td>
                    <Td textAlign="center" color="green.600"><Icon as={FiCheck} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                    <Td textAlign="center" color="red.400"><Icon as={FiX} /></Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>
          </Box>

          <Divider />

          {/* Pricing Comparison */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>Pricing Comparison</Heading>
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Service</Th>
                    <Th>Fee Model</Th>
                    <Th>Min Deposit</Th>
                    <Th isNumeric>Cost on $100K</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr bg="green.50" fontWeight="bold">
                    <Td>Safe Crypto</Td>
                    <Td>2% deposit + 0.5% annual</Td>
                    <Td>None</Td>
                    <Td isNumeric>$2,000 + $500/yr</Td>
                  </Tr>
                  <Tr>
                    <Td>Coinbase Custody</Td>
                    <Td>0.5% annual</Td>
                    <Td>$1M+</Td>
                    <Td isNumeric color="gray.400">N/A (below min)</Td>
                  </Tr>
                  <Tr bg="gray.50">
                    <Td>BitGo</Td>
                    <Td>0.25-0.5% annual</Td>
                    <Td>$100K+</Td>
                    <Td isNumeric>$250-500/yr</Td>
                  </Tr>
                  <Tr>
                    <Td>Fireblocks</Td>
                    <Td>Flat $10K+/month</Td>
                    <Td>Enterprise</Td>
                    <Td isNumeric color="gray.400">$120K+/yr</Td>
                  </Tr>
                  <Tr bg="gray.50">
                    <Td>Casa</Td>
                    <Td>$130-$2,500/yr flat</Td>
                    <Td>None</Td>
                    <Td isNumeric>$130-$2,500/yr</Td>
                  </Tr>
                  <Tr>
                    <Td>Anchorage</Td>
                    <Td>0.4-0.8% annual</Td>
                    <Td>$5M+</Td>
                    <Td isNumeric color="gray.400">N/A (below min)</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>
            <Text fontSize="sm" color="gray.500" mt={2}>
              Safe Crypto's 2% deposit fee is higher upfront, but you get the only custody solution with mandatory time-locks AND human guardian verification. No one else protects against social engineering.
            </Text>
          </Box>

          <Divider />

          {/* Competitor Deep Dives */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={4}>Where Each Competitor Falls Short</Heading>
            <VStack spacing={4} align="stretch">
              {competitors.map((c, i) => (
                <Box key={i} p={4} borderRadius="md" border="1px solid" borderColor="gray.200">
                  <HStack mb={2}>
                    <Text fontWeight="bold" color="gray.700">{c.name}</Text>
                    <Badge colorScheme="gray" fontSize="xs">{c.audience}</Badge>
                    <Badge colorScheme="blue" fontSize="xs">{c.fee}</Badge>
                  </HStack>
                  <Text fontSize="sm" color="gray.600">{c.weakness}</Text>
                </Box>
              ))}
            </VStack>
          </Box>

          <Divider />

          {/* Why We Win */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>Why Safe Crypto Wins</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box p={4} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                <HStack mb={2}>
                  <Icon as={FiClock} color="green.600" />
                  <Text fontWeight="bold" color="green.700">Time is the Defence</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  No other solution mandates a 10-day withdrawal delay. Social engineering attacks work because they create urgency. A mandatory delay makes urgency impossible — the attacker can't rush you because the system won't let anyone rush.
                </Text>
              </Box>
              <Box p={4} bg="purple.50" borderRadius="md" border="1px solid" borderColor="purple.200">
                <HStack mb={2}>
                  <Icon as={FiUsers} color="purple.600" />
                  <Text fontWeight="bold" color="purple.700">Humans Verify Humans</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Multi-sig and MPC split keys across devices, but a skilled social engineer can trick someone into approving on multiple devices. Guardian networks add independent human judgement — 2-of-3 trusted people who don't know each other must separately confirm your identity.
                </Text>
              </Box>
              <Box p={4} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200">
                <HStack mb={2}>
                  <Icon as={FiShield} color="orange.600" />
                  <Text fontWeight="bold" color="orange.700">Built from Real Attacks</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Every feature exists because someone actually lost money. Ed was tricked into entering his hardware wallet seed words into a fake website by a caller claiming he'd been hacked. Tom's Google account was compromised after his password leaked online. These aren't theoretical threat models — the product is designed around what attackers actually do.
                </Text>
              </Box>
              <Box p={4} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                <HStack mb={2}>
                  <Icon as={FiDollarSign} color="blue.600" />
                  <Text fontWeight="bold" color="blue.700">Accessible Pricing</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Most institutional custody requires $100K-$5M minimum deposits. Safe Crypto has no minimum — protect $100 or $10M with the same security. The 2% deposit + 0.5% annual fee scales with your assets, not with enterprise contracts.
                </Text>
              </Box>
            </SimpleGrid>
          </Box>

          <Divider />

          {/* CTA */}
          <Box bg="green.50" p={5} borderRadius="md" border="1px solid" borderColor="green.200">
            <Text fontWeight="bold" color="green.700" mb={2}>
              The bottom line: every competitor protects your keys. Only Safe Crypto protects you.
            </Text>
            <Link as={RouterLink} to="/updates/safe-crypto-custody" _hover={{ textDecoration: "none" }}>
              <Button colorScheme="blue" size="sm" leftIcon={<FiArrowLeft />}>
                Back to Safe Crypto Custody
              </Button>
            </Link>
          </Box>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </>
  );
};

export default SafeCryptoCompetition;
